/**
 * Lógica de transición entre vistas del CRT.
 *
 * `processInput(state, input)` recibe lo que el usuario tecleó al
 * pulsar Enter en una vista activa y devuelve el efecto: avanzar a
 * otra vista, mostrar un error en pantalla, redirigir, o terminar el
 * flujo.
 *
 * No mutamos `state.view` aquí — el caller (input handler) aplica el
 * efecto. Mantener esta función pura simplifica testing futuro.
 */

import type { LegalDocumentId } from '../legal/content.js';
import * as api from './api.js';
import type { TimeOfDayPreference } from './crt-controls.js';
import { messageFor } from './messages.js';
import { listProfiles, type MonitorProfileId } from './monitor-profiles.js';
import type { AppState, ViewId } from './state.js';

const CALLSIGN_RE = /^[A-Za-z0-9_-]{3,16}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_MIN = 10;

export type Effect =
  | { kind: 'goto'; view: ViewId }
  | { kind: 'error'; text: string; gotoView?: ViewId }
  | { kind: 'message'; text: string; gotoView: ViewId }
  | { kind: 'redirect'; url: string; greeting?: string }
  | { kind: 'logout' }
  | { kind: 'apply_profile'; profileId: MonitorProfileId }
  | { kind: 'apply_time_of_day'; pref: TimeOfDayPreference }
  | { kind: 'show_about_doc'; docId: LegalDocumentId }
  | { kind: 'noop' };

/** Texto del prompt actual ("CALLSIGN:", "PASSWORD:", etc.). */
export function promptForView(view: ViewId): string {
  switch (view) {
    case 'menu': return 'A>';
    case 'login_callsign':
    case 'register_callsign':
      return 'CALLSIGN:';
    case 'login_password':
    case 'register_password':
      return 'PASSWORD:';
    case 'register_email':
    case 'verify_email_resend':
    case 'recovery_request':
      return 'EMAIL:';
    case 'register_password_confirm':
      return 'PASSWORD (CONFIRMAR):';
    case 'recovery_set_password':
      return 'NUEVA PASSWORD:';
    case 'register_turnstile':
      return 'PULSA ENTER CUANDO HAYAS COMPLETADO LA VERIFICACIÓN';
    case 'verify_email_checking':
      return 'VERIFICANDO EMAIL...';
    case 'register_done':
    case 'verify_email_done':
    case 'verify_email_resent':
    case 'recovery_sent':
      return 'PULSA [ESC] PARA VOLVER AL MENÚ';
    case 'about_doc':
      return 'PULSA [ESC] PARA VOLVER';
    case 'config':
    case 'config_screen_profile':
    case 'config_time_of_day':
    case 'about':
      return 'A>';
    case 'login_busy':
    case 'register_busy':
      return 'PROCESANDO...';
  }
}

export function isPasswordView(view: ViewId): boolean {
  return (
    view === 'login_password' ||
    view === 'register_password' ||
    view === 'register_password_confirm' ||
    view === 'recovery_set_password'
  );
}

/* ─── Transiciones desde el menú principal ────────────────────────────── */

/**
 * Sub-menú de CONFIG (vista `config`).
 */
export function selectConfigOption(key: string): Effect {
  switch (key) {
    case '1': return { kind: 'goto', view: 'config_screen_profile' };
    case '2': return { kind: 'goto', view: 'config_time_of_day' };
    case '3': return { kind: 'goto', view: 'menu' };
    default:  return { kind: 'noop' };
  }
}

/**
 * Sub-menú de ACERCA DE (vista `about`). Las opciones 1-4 abren cada
 * texto legal en la vista `about_doc`; la 5 vuelve al menú principal.
 */
export function selectAboutOption(key: string): Effect {
  switch (key) {
    case '1': return { kind: 'show_about_doc', docId: 'aviso-legal' };
    case '2': return { kind: 'show_about_doc', docId: 'privacidad' };
    case '3': return { kind: 'show_about_doc', docId: 'terminos' };
    case '4': return { kind: 'show_about_doc', docId: 'cookies' };
    case '5': return { kind: 'goto', view: 'menu' };
    default:  return { kind: 'noop' };
  }
}

/**
 * Selector de MODO DÍA/TARDE/NOCHE (vista `config_time_of_day`). Igual que
 * el selector de perfil: el (X) se mueve sin salir de la vista; ESC
 * vuelve al sub-menú CONFIG.
 */
export function selectTimeOfDayOption(key: string, current: TimeOfDayPreference): Effect {
  switch (key) {
    case '1': return { kind: 'apply_time_of_day', pref: 'auto' };
    case '2': return { kind: 'apply_time_of_day', pref: 'day' };
    case '3': return { kind: 'apply_time_of_day', pref: 'dusk' };
    case '4': return { kind: 'apply_time_of_day', pref: 'night' };
    case '5': return { kind: 'apply_time_of_day', pref: nextCyclePref(current) };
    default:  return { kind: 'noop' };
  }
}

/**
 * Rota el período del modo CICLO: si aún no está activo, entra con
 * 10s (default); si ya estaba en 10s/30s pasa al siguiente; en 60s
 * vuelve a 10s.
 */
function nextCyclePref(current: TimeOfDayPreference): TimeOfDayPreference {
  if (current === 'cycle-10') return 'cycle-30';
  if (current === 'cycle-30') return 'cycle-60';
  if (current === 'cycle-60') return 'cycle-10';
  return 'cycle-10';
}

/**
 * Selector de PERFIL DE PANTALLA (vista `config_screen_profile`).
 * Las teclas 1..N corresponden al orden de `listProfiles()`. Al pulsar
 * un número válido, aplica el perfil y persiste — el (X) del listado
 * se mueve al nuevo perfil sin salir de la vista (el usuario puede
 * probar varios consecutivos). ESC vuelve al sub-menú CONFIG.
 */
export function selectProfileOption(key: string): Effect {
  const profiles = listProfiles();
  const idx = Number(key) - 1;
  if (!Number.isInteger(idx) || idx < 0 || idx >= profiles.length) {
    return { kind: 'noop' };
  }
  const target = profiles[idx];
  if (!target) return { kind: 'noop' };
  return { kind: 'apply_profile', profileId: target.id };
}

export function selectMenuOption(key: string, state: AppState): Effect {
  switch (key) {
    case '1':
      // Si ya hay sesión activa, la opción 1 es LOGOUT.
      return state.authenticatedUser
        ? { kind: 'logout' }
        : { kind: 'goto', view: 'login_callsign' };
    case '2': return { kind: 'goto', view: 'register_callsign' };
    case '3': return { kind: 'redirect', url: '/recovery.html' };
    case '4': return { kind: 'goto', view: 'config' };
    case '5': return { kind: 'goto', view: 'about' };
    case '6': return { kind: 'redirect', url: '/' };
    default:  return { kind: 'noop' };
  }
}

/* ─── Procesa Enter en cada vista ─────────────────────────────────────── */

export async function processInput(
  state: AppState,
  rawInput: string,
): Promise<Effect> {
  const input = rawInput.trim();

  switch (state.view) {
    // ── LOGIN ──
    case 'login_callsign': {
      if (!CALLSIGN_RE.test(input)) {
        return { kind: 'error', text: messageFor('callsign') };
      }
      state.loginData.callsign = input;
      return { kind: 'goto', view: 'login_password' };
    }
    case 'login_password': {
      if (rawInput.length < PASSWORD_MIN) {
        return { kind: 'error', text: messageFor('password') };
      }
      const callsign = state.loginData.callsign;
      if (!callsign) return { kind: 'goto', view: 'login_callsign' };
      const result = await api.login({ callsign, password: rawInput });
      if (!result.ok) {
        return {
          kind: 'error',
          text: messageFor(result.code, result.error),
          gotoView: 'login_callsign',
        };
      }
      state.authenticatedUser = result.data.user;
      return {
        kind: 'redirect',
        url: '/',
        greeting: `BIENVENIDO, ${result.data.user.callsign.toUpperCase()}.`,
      };
    }

    // ── REGISTER ──
    case 'register_callsign': {
      if (!CALLSIGN_RE.test(input)) {
        return { kind: 'error', text: messageFor('callsign') };
      }
      state.registerData.callsign = input;
      return { kind: 'goto', view: 'register_email' };
    }
    case 'register_email': {
      if (!EMAIL_RE.test(input)) {
        return { kind: 'error', text: messageFor('email') };
      }
      state.registerData.email = input.toLowerCase();
      return { kind: 'goto', view: 'register_password' };
    }
    case 'register_password': {
      if (rawInput.length < PASSWORD_MIN) {
        return { kind: 'error', text: messageFor('password') };
      }
      state.registerData.password = rawInput;
      return { kind: 'goto', view: 'register_password_confirm' };
    }
    case 'register_password_confirm': {
      if (rawInput !== state.registerData.password) {
        return { kind: 'error', text: messageFor('passwords_mismatch') };
      }
      return { kind: 'goto', view: 'register_turnstile' };
    }
    case 'register_turnstile': {
      // El Enter en esta vista valida que tengamos token y submitea.
      if (!state.turnstileToken) {
        return { kind: 'error', text: messageFor('cfTurnstileResponse') };
      }
      const { callsign, email, password } = state.registerData;
      if (!callsign || !email || !password) {
        return { kind: 'goto', view: 'register_callsign' };
      }
      const result = await api.register({
        callsign,
        email,
        password,
        cfTurnstileResponse: state.turnstileToken,
      });
      if (!result.ok) {
        // Tokens Turnstile son single-use → invalidamos el guardado.
        state.turnstileToken = null;
        return {
          kind: 'error',
          text: messageFor(result.code, result.error),
          gotoView: 'register_turnstile',
        };
      }
      return { kind: 'goto', view: 'register_done' };
    }

    // ── VERIFICACIÓN DE EMAIL ──
    case 'verify_email_resend': {
      if (!EMAIL_RE.test(input)) {
        return { kind: 'error', text: messageFor('email') };
      }
      const result = await api.resendVerification({
        email: input.toLowerCase(),
      });
      if (!result.ok) {
        return { kind: 'error', text: messageFor(result.code, result.error) };
      }
      return { kind: 'goto', view: 'verify_email_resent' };
    }

    // ── RECUPERAR CONTRASEÑA ──
    case 'recovery_request': {
      if (!EMAIL_RE.test(input)) {
        return { kind: 'error', text: messageFor('email') };
      }
      const result = await api.requestPasswordReset({
        email: input.toLowerCase(),
      });
      if (!result.ok) {
        return { kind: 'error', text: messageFor(result.code, result.error) };
      }
      return { kind: 'goto', view: 'recovery_sent' };
    }
    case 'recovery_set_password': {
      if (rawInput.length < PASSWORD_MIN) {
        return { kind: 'error', text: messageFor('password') };
      }
      const token = state.resetToken;
      if (!token) {
        return {
          kind: 'error',
          text: messageFor('invalid_token'),
          gotoView: 'recovery_request',
        };
      }
      const result = await api.confirmPasswordReset({
        token,
        newPassword: rawInput,
      });
      if (!result.ok) {
        // Si el token ya no sirve, devolvemos al formulario de solicitud
        // para que el usuario pida uno nuevo; si el fallo es la propia
        // contraseña, se reintenta en la misma vista.
        const tokenDead =
          result.code === 'invalid_token' ||
          result.code === 'token_expired' ||
          result.code === 'token_used';
        return {
          kind: 'error',
          text: messageFor(result.code, result.error),
          gotoView: tokenDead ? 'recovery_request' : undefined,
        };
      }
      return {
        kind: 'redirect',
        url: '/login.html',
        greeting: 'CONTRASEÑA ACTUALIZADA. INICIA SESIÓN.',
      };
    }

    // ── VISTAS SIN INPUT POR ENTER ──
    case 'about':
    case 'about_doc':
    case 'register_done':
    case 'verify_email_checking':
    case 'verify_email_done':
    case 'verify_email_resent':
    case 'recovery_sent':
    case 'login_busy':
    case 'register_busy':
    case 'menu':
    case 'config':
    case 'config_screen_profile':
    case 'config_time_of_day':
      // No procesan input por Enter — vuelven con [ESC], se manejan
      // por teclas numéricas en input-handler, o se mueven solas.
      return { kind: 'noop' };
  }
}
